import React, { useEffect, useState, useCallback } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL;

const LEVELS = ['MASTER', 'FAMILY', 'SPECIFIC'];
const DOC_TYPES = [
  'Quality Procedure', 'Inspection Procedure', 'Test Procedure', 'Work Instruction',
];

export default function QualityTemplatesPage() {
  const { getToken } = useAuth();
  const { user } = useUser();
  const role = String(user?.publicMetadata?.role || '').toLowerCase().trim();
  const isAdmin = role === 'admin';
  const canManage = ['admin', 'quality-manager', 'production-engineer', 'service'].includes(role);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedMsg, setSeedMsg] = useState('');
  const [form, setForm] = useState({ templateName: '', documentLevel: 'SPECIFIC', documentType: 'Inspection Procedure', markdownTemplate: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const apiCall = useCallback(async (path, method = 'GET', body = null) => {
    const token = await getToken();
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      ...(body ? { body: JSON.stringify(body) } : {}),
    });
    return res.json();
  }, [getToken]);

  const load = useCallback(async (autoSeed = false) => {
    setLoading(true);
    try {
      const d = await apiCall('/quality/templates');
      if (d.ok) {
        if (autoSeed && d.templates?.length === 0) {
          // First time — silently seed the 4 default templates
          await apiCall('/quality/templates/seed', 'POST');
          const d2 = await apiCall('/quality/templates');
          if (d2.ok) setTemplates(d2.templates || []);
        } else {
          setTemplates(d.templates || []);
        }
      }
    } catch {
      // backend not yet reachable
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  const canSeed = canManage;
  useEffect(() => { load(canSeed); }, [load, canSeed]);

  const handleSeed = async (force = false) => {
    setSeeding(true); setSeedMsg('');
    try {
      const d = await apiCall('/quality/templates/seed', 'POST', force ? { force: true } : {});
      if (d.ok) {
        const parts = [];
        if (d.created) parts.push(`Created ${d.created}`);
        if (d.updated) parts.push(`Updated ${d.updated}`);
        if (d.skipped) parts.push(`Skipped ${d.skipped}`);
        setSeedMsg(parts.join(', ') || 'Done');
        load();
      } else setSeedMsg(d.message || 'Seed failed');
    } catch {
      setSeedMsg('Request failed — check connection');
    } finally {
      setSeeding(false);
    }
  };

  const handleSave = async () => {
    if (!form.templateName.trim()) return setError('Name required');
    setSaving(true); setError('');
    try {
      const d = editing && selected
        ? await apiCall(`/quality/templates/${selected._id}`, 'PATCH', form)
        : await apiCall('/quality/templates', 'POST', form);
      if (d.ok) { setEditing(false); setSelected(null); load(); }
      else setError(d.message || 'Save failed');
    } catch (err) {
      setError(err?.message || 'Request failed — check connection');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Archive template "${t.templateName}"?`)) return;
    await apiCall(`/quality/templates/${t._id}`, 'DELETE');
    load();
  };

  const startEdit = (t) => {
    setForm({ templateName: t.templateName, documentLevel: t.documentLevel, documentType: t.documentType, markdownTemplate: t.markdownTemplate });
    setSelected(t);
    setEditing(true);
    setError('');
  };

  const inputStyle = { background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text)', borderRadius: 6, padding: '7px 10px', fontSize: 13, width: '100%', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ni-text-muted)', marginBottom: 5 };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Document Templates</h2>
        <div style={{ display: 'flex', gap: 10 }}>
          {canManage && (
            <>
              <button onClick={() => handleSeed(false)} disabled={seeding} style={{ background: 'none', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text-muted)', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontSize: 13 }}>
                {seeding ? 'Seeding...' : 'Seed Defaults'}
              </button>
              <button onClick={() => handleSeed(true)} disabled={seeding} style={{ background: 'none', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text-muted)', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontSize: 13 }}>
                {seeding ? 'Updating...' : 'Re-seed (update existing)'}
              </button>
            </>
          )}
          {canManage && (
            <button
              onClick={() => { setForm({ templateName: '', documentLevel: 'SPECIFIC', documentType: 'Inspection Procedure', markdownTemplate: '' }); setSelected(null); setEditing(true); setError(''); }}
              style={{ background: 'var(--ni-green)', color: '#000', border: 'none', borderRadius: 6, padding: '7px 16px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
            >
              + New Template
            </button>
          )}
        </div>
      </div>

      {seedMsg && <div style={{ marginBottom: 16, color: 'var(--ni-green)', fontSize: 13 }}>{seedMsg}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: editing ? '1fr 480px' : '1fr', gap: 20, alignItems: 'start' }}>
        {/* Template list */}
        <div style={{ background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', borderRadius: 10, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 32, color: 'var(--ni-text-muted)', textAlign: 'center' }}>Loading...</div>
          ) : templates.length === 0 ? (
            <div style={{ padding: 32, color: 'var(--ni-text-muted)', textAlign: 'center', fontSize: 14 }}>
              No templates. {canManage && <button onClick={() => handleSeed(false)} style={{ background: 'none', border: 'none', color: 'var(--ni-green)', cursor: 'pointer' }}>Seed defaults.</button>}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ color: 'var(--ni-text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', background: 'rgba(0,0,0,0.2)' }}>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Name</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Level</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Type</th>
                  {canManage && <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {templates.map(t => (
                  <tr key={t._id} style={{ borderTop: '1px solid var(--ni-border-soft)', background: selected?._id === t._id ? 'rgba(70,187,137,0.06)' : 'transparent' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600 }}>{t.templateName}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--ni-text-muted)' }}>{t.documentLevel}</td>
                    <td style={{ padding: '12px 16px', color: 'var(--ni-text-muted)', fontSize: 12 }}>{t.documentType}</td>
                    {canManage && (
                      <td style={{ padding: '12px 16px' }}>
                        <button onClick={() => startEdit(t)} style={{ background: 'none', border: 'none', color: 'var(--ni-green)', cursor: 'pointer', fontSize: 12, marginRight: 12 }}>Edit</button>
                        {isAdmin && <button onClick={() => handleDelete(t)} style={{ background: 'none', border: 'none', color: '#EC2124', cursor: 'pointer', fontSize: 12 }}>Archive</button>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Edit panel */}
        {editing && (
          <div style={{ background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', borderRadius: 10, padding: 24 }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 15, fontWeight: 800 }}>{selected ? 'Edit Template' : 'New Template'}</h3>
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Name *</label>
              <input style={inputStyle} value={form.templateName} onChange={e => setForm(f => ({ ...f, templateName: e.target.value }))} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={labelStyle}>Level</label>
                <select style={inputStyle} value={form.documentLevel} onChange={e => setForm(f => ({ ...f, documentLevel: e.target.value }))}>
                  {LEVELS.map(l => <option key={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Type</label>
                <select style={inputStyle} value={form.documentType} onChange={e => setForm(f => ({ ...f, documentType: e.target.value }))}>
                  {DOC_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Markdown Template</label>
              <textarea
                style={{ ...inputStyle, minHeight: 320, resize: 'vertical', fontFamily: 'monospace', fontSize: 12, lineHeight: 1.5 }}
                value={form.markdownTemplate}
                onChange={e => setForm(f => ({ ...f, markdownTemplate: e.target.value }))}
                placeholder="Use {{documentTitle}}, {{documentNumber}}, {{revision}}, etc. as placeholders"
              />
              <div style={{ fontSize: 11, color: 'var(--ni-text-muted)', marginTop: 4 }}>
                Placeholders: {`{{documentTitle}} {{documentNumber}} {{revision}} {{owner}} {{effectiveDate}} {{productFamily}} {{productNumber}} {{supplier}} {{processArea}}`}
              </div>
            </div>
            {error && <div style={{ color: '#EC2124', fontSize: 13, marginBottom: 10 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleSave} disabled={saving} style={{ background: 'var(--ni-green)', color: '#000', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button onClick={() => { setEditing(false); setSelected(null); }} style={{ background: 'none', border: '1px solid var(--ni-border-soft)', color: 'var(--ni-text-muted)', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', fontSize: 13 }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
